import { RailsFlashMessages } from "@circle-react/components/RailsFlashMessages";
import { I18nProvider } from "@circle-react/providers/I18nProvider";
import { QueryClientProvider } from "@circle-react/providers/QueryClient";
import { createQueryConfig } from "@circle-react/providers/QueryClient/reactQueryConfig";
import { applyV2Colors } from "@circle-react/providers/Theme/applyV2Colors";
import { ThemeProvider } from "@circle-react/providers/Theme/ThemeProvider";
import type { ThemeObject } from "@circle-react/types/Theme";
import { CompassProvider } from "@circleco/compass/context/CompassProvider";
import { StrictMode, createContext, useContext, useState } from "react";
import { QueryClient } from "react-query";

// eslint-disable-next-line no-restricted-imports -- Compass has no ToastProvider yet; auth pages still use the legacy provider.
import { ToastProvider } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { OtpChallengeProvider } from "@/react/contexts/OtpChallengeContext";
import { PunditUserContextProvider } from "@/react/contexts/punditUserContext";
import { AuthenticationAppRouter } from "@/react/routes/Authentication/AuthenticationAppRouter";

const CurrentCommunityContext = createContext<any>({
  updateCurrentCommunity: () => {},
});
CurrentCommunityContext.displayName = "CurrentCommunityContext";

export const useCurrentCommunityContext = () =>
  useContext(CurrentCommunityContext);

const queryClient = new QueryClient(createQueryConfig());

export interface AuthenticationAppProps {
  alert: string;
  authProviderAuthorizationUrl: string;
  authProviderName: string;
  authProviderSignInWithCircleLinkLabel: string;
  brandColor: any;
  brandTextColor: any;
  can_current_community_support_custom_login: boolean;
  communityThemeSettings: any;
  current_user: any;
  currentCommunity: any;
  currentCommunityPolicies: any;
  errors: any;
  googleClientId?: string | null;
  googleOneTapNonce?: string | null;
  invitationToken: string;
  is_passwordless_signup?: boolean | null;
  is_reset_password_user_passwordless?: boolean | null;
  is_signup_password_matching: boolean;
  isCaiSuf: boolean;
  notice: string;
  promotion_details: any;
  signUpOptions: any;
  suggestedLanguage: string;
  user_email: string;
  user_is_passwordless?: boolean | null;
}

export const AuthenticationApp = ({
  currentCommunity,
  currentCommunityPolicies,
  communityThemeSettings = {},
  brandColor = {
    light: "",
    dark: "",
  },
  brandTextColor = {
    light: "",
    dark: "",
  },
  invitationToken,
  signUpOptions,
  notice,
  alert,
  errors,
  user_email: userEmail,
  current_user: currentUser,
  is_signup_password_matching: isSignupPasswordMatching,
  user_is_passwordless: userIsPasswordless,
  is_reset_password_user_passwordless: isResetPasswordUserPasswordless,
  is_passwordless_signup: isPasswordlessSignup,
  can_current_community_support_custom_login:
    canCurrentCommunitySupportCustomLogin,
  authProviderAuthorizationUrl,
  authProviderName,
  authProviderSignInWithCircleLinkLabel,
  promotion_details,
  suggestedLanguage,
  isCaiSuf,
  googleClientId,
  googleOneTapNonce,
}: AuthenticationAppProps) => {
  const theme: ThemeObject = {
    ...communityThemeSettings,
    brand_color: brandColor,
    brand_text_color: brandTextColor,
    current_theme: "default",
    is_custom: false,
  };

  applyV2Colors();

  const [community, setCommunity] = useState(currentCommunity);

  return (
    <StrictMode>
      <CompassProvider>
        <I18nProvider>
          <QueryClientProvider client={queryClient}>
            <PunditUserContextProvider>
              <CurrentCommunityContext.Provider
                value={{
                  currentCommunity: community,
                  updateCurrentCommunity: setCommunity,
                  currentCommunityPolicies,
                  invitationToken,
                  signUpOptions,
                  errors,
                  currentUser,
                  userEmail,
                  isSignupPasswordMatching,
                  isUserPasswordless: userIsPasswordless,
                  isResetPasswordUserPasswordless,
                  isPasswordlessSignup,
                  canCurrentCommunitySupportCustomLogin,
                  authProviderAuthorizationUrl,
                  authProviderName,
                  suggestedLanguage,
                  authProviderSignInWithCircleLinkLabel,
                  promotion_details,
                  isCaiSuf,
                  googleClientId,
                  googleOneTapNonce,
                }}
              >
                <ThemeProvider theme={theme}>
                  <ToastProvider>
                    <OtpChallengeProvider>
                      <RailsFlashMessages notice={notice} alert={alert} />
                      <AuthenticationAppRouter
                        hasEmail={Boolean(errors?.email && userEmail)}
                      />
                    </OtpChallengeProvider>
                  </ToastProvider>
                </ThemeProvider>
              </CurrentCommunityContext.Provider>
            </PunditUserContextProvider>
          </QueryClientProvider>
        </I18nProvider>
      </CompassProvider>
    </StrictMode>
  );
};
